import { useEffect, useState, type CSSProperties } from 'react'

import { renderAs, type IntrinsicTag, type PolymorphicProps } from '@/components/motion/polymorphic'
import { closeStaggerAfterEntrance, staggerStyle } from '@/lib/motion/stagger'
import { useStaggerRows } from '@/lib/motion/use-stagger-rows'
import { cn } from '@/lib/utils'

interface StaggerOwnProps {
  /**
   * False holds the entrance back, and its close with it: a table's skeleton
   * rows, a pending page's blocks. The children that are there when it turns
   * true are the ones that enter. Default true.
   */
  active?: boolean | undefined
  /**
   * Close only once every finite animation under the container has ended,
   * not just its children's fade-up: for a container whose `data-entered` also
   * ends the marks inside its items (DataTable's body, see TABLE MARKS in
   * globals.css), which must not land while a sparkline is still drawing in.
   */
  subtree?: boolean | undefined
  /**
   * Rows instead of children (`useStaggerRows`): looks through a form and its
   * field group so each field enters on its own, and every batch of rows that
   * joins the container (an outlet swapping its screen in) enters from zero.
   * It never closes, and `active` and `subtree` do not apply.
   */
  rows?: boolean | undefined
  className?: string | undefined
  style?: CSSProperties | undefined
  /** The container's ref is the kit's: it is what the close is measured on. */
  ref?: never
}

export type StaggerProps<T extends IntrinsicTag> = PolymorphicProps<T, StaggerOwnProps>

/**
 * A container whose children enter one after another, 40ms apart and capped at
 * 320ms (the `stagger` utility, globals.css), once.
 *
 * Once the entrance has played it marks itself `data-entered`
 * (`closeStaggerAfterEntrance`) and the rule stops applying, so a child that
 * mounts later — a message, a card that appears with its data, a sorted row —
 * just appears, or plays its own animation. The rule is zero-specificity, so
 * any child's own `animate-*` class (an `animate-none` on a nested `Stagger`)
 * wins over it. Reduced motion collapses it through the global rule.
 */
export default function Stagger<T extends IntrinsicTag = 'div'>({
  as,
  active = true,
  subtree = false,
  rows = false,
  className,
  ...rest
}: StaggerProps<T>) {
  // State, not a ref object, which react-hooks/refs will not let reach
  // `renderAs`. Set in the commit that mounts the element, whose follow-up
  // render React runs before the browser paints, so `rows` still tags the rows
  // before the first frame.
  const [container, setContainer] = useState<HTMLElement | null>(null)
  useStaggerRows(container, rows)

  useEffect(() => {
    if (rows || !active || container === null) return
    return closeStaggerAfterEntrance(container, { subtree })
  }, [container, rows, active, subtree])

  return renderAs(as ?? 'div', {
    ...rest,
    ref: setContainer,
    className: cn(!rows && active && 'stagger', className) || undefined,
  })
}

interface StaggerItemOwnProps {
  /** Its place in the list, which its delay follows. */
  index: number
  className?: string | undefined
  style?: CSSProperties | undefined
}

/**
 * One item of a list whose items are not all children of one container — a
 * virtualised list, rows split across groups, a panel beside a nested list —
 * on the same timing as `Stagger`, placed by `index` (`stagger-item`,
 * globals.css). It plays on every mount of the item, so a list that must not
 * replay it renders it only for the entrance.
 */
function StaggerItem<T extends IntrinsicTag = 'div'>({
  as,
  index,
  className,
  style,
  ...rest
}: PolymorphicProps<T, StaggerItemOwnProps>) {
  return renderAs(as ?? 'div', {
    ...rest,
    className: cn('stagger-item', className),
    style: { ...staggerStyle(index), ...style },
  })
}

Stagger.Item = StaggerItem
